<template>
  <div class="booking-analytics text-left mx-5 pb-4 mt-2">
    <!-- Header -->
    <div class="text-center mb-5">
      <h1 class="text-gray-500 text-3xl font-weight-bold">Booking Analytics</h1>
    </div>

    <!-- Row 1: Summary Cards (2 cards) -->
    <b-row class="mt-4">
      <!-- Outstanding Balances by Destination and Avg Days Past Due -->
      <b-col cols="12" sm="6" lg="8" class="mb-3">
        <div class="shadow bg-white rounded-lg px-4 py-3 h-100">
          <b-row>
            <b-col cols="12" md="6" class="mb-3 mb-md-0">
              <div
                class="text-gray-400 font-weight-bold text-uppercase text-xs text-center"
              >
                Outstanding Balances by Destination
              </div>
              <div
                class="position-relative mt-3"
                style="width: 100%; height: 250px"
              >
                <canvas id="balance-by-destination-chart"></canvas>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div
                class="text-gray-400 font-weight-bold text-uppercase text-xs text-center"
              >
                Avg Days Past Due
              </div>
              <div
                class="position-relative mt-3"
                style="width: 100%; height: 250px"
              >
                <canvas id="avg-days-past-due-chart"></canvas>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>

      <!-- Top 5 Overdue Bookings -->
      <b-col cols="12" sm="6" lg="4" class="mb-3">
        <div class="shadow bg-white rounded-lg px-4 py-3 h-100">
          <div
            class="text-gray-400 font-weight-bold text-uppercase text-xs mb-3"
          >
            Top 5 Overdue Bookings
          </div>
          <div class="top-bookings-list">
            <div
              v-for="(booking, idx) in topOverdueBookings"
              :key="idx"
              class="top-booking-item mb-2"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <div class="text-gray-700 text-sm font-weight-bold">
                    {{ booking.client }}
                  </div>
                  <div class="text-gray-500 text-xs">
                    {{ booking.manifestId }}
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-gray-700 text-sm font-weight-bold">
                    {{ formatCurrency(booking.due) }}
                  </div>
                  <div class="text-gray-500 text-xs">
                    {{ booking.daysPastDue }} days
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Row 2: Revenue Cards (2 cards) -->
    <b-row class="mt-2">
      <!-- Total Revenue by Destination and Top Payments by Destination -->
      <b-col cols="12" sm="6" lg="8" class="mb-3">
        <div class="shadow bg-white rounded-lg px-4 py-3 h-100">
          <b-row>
            <b-col cols="12" md="6" class="mb-3 mb-md-0">
              <div
                class="text-gray-400 font-weight-bold text-uppercase text-xs text-center"
              >
                Total Revenue by Destination
              </div>
              <div
                class="position-relative mt-3"
                style="width: 100%; height: 250px"
              >
                <canvas id="revenue-by-destination-chart"></canvas>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div
                class="text-gray-400 font-weight-bold text-uppercase text-xs text-center"
              >
                Top Payments by Destination
              </div>
              <div
                class="position-relative mt-3"
                style="width: 100%; height: 250px"
              >
                <canvas id="top-payments-chart"></canvas>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>

      <!-- Top 5 Paid Bookings -->
      <b-col cols="12" sm="6" lg="4" class="mb-3">
        <div class="shadow bg-white rounded-lg px-4 py-3 h-100">
          <div
            class="text-gray-400 font-weight-bold text-uppercase text-xs mb-3"
          >
            Top 5 Paid Bookings
          </div>
          <div class="top-bookings-list">
            <div
              v-for="(booking, idx) in topPaidBookings"
              :key="idx"
              class="top-booking-item mb-2"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <div class="text-gray-700 text-sm font-weight-bold">
                    {{ booking.client }}
                  </div>
                  <div class="text-gray-500 text-xs">
                    {{ booking.manifestId }}
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-gray-700 text-sm font-weight-bold">
                    {{ formatCurrency(booking.received) }}
                  </div>
                  <div class="text-gray-500 text-xs">
                    {{ formatDate(booking.dateReceived) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Filters and Table Row -->
    <b-row class="mt-4">
      <!-- Filters Sidebar -->
      <b-col xs="12" md="12" lg="3" class="mb-4">
        <div class="shadow bg-white rounded-lg px-4 py-3 mb-3">
          <h3 class="text-gray-700 font-weight-bold text-lg mb-3">Filters</h3>

          <!-- Search Manifest ID -->
          <div class="mb-4">
            <label class="text-gray-600 text-sm font-weight-bold mb-2 d-block">
              Manifest ID
            </label>
            <b-form-input
              v-model="filters.manifestId"
              placeholder="Search Manifest ID"
              class="form-control"
            ></b-form-input>
          </div>

          <!-- Date Range -->
          <div class="mb-4">
            <label class="text-gray-600 text-sm font-weight-bold mb-2 d-block">
              Date Range
            </label>
            <b-form-radio-group
              v-model="filters.dateRange"
              :options="dateRangeOptions"
              class="text-sm"
            ></b-form-radio-group>
          </div>

          <!-- Payment Method -->
          <div class="mb-4">
            <label class="text-gray-600 text-sm font-weight-bold mb-2 d-block">
              Payment Method
            </label>
            <div>
              <b-form-checkbox
                v-for="method in paymentMethods"
                :key="method.value"
                v-model="filters.paymentMethods"
                :value="method.value"
                class="text-sm mb-2"
              >
                {{ method.text }}
              </b-form-checkbox>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Data Table -->
      <b-col xs="12" md="12" lg="9">
        <div class="shadow bg-white rounded-lg px-4 py-3 mb-3">
          <b-table
            :items="filteredTableData"
            :fields="tableFields"
            responsive="lg"
            :hover="true"
            class="analytics-table"
            style="font-size: smaller"
          >
            <template v-slot:cell(due)="data">
              {{ formatCurrency(data.value) }}
            </template>
            <template v-slot:cell(received)="data">
              {{ formatCurrency(data.value) }}
            </template>
            <template v-slot:cell(dateReceived)="data">
              {{ data.value ? formatDate(data.value) : "-" }}
            </template>
            <template v-slot:cell(invoiceId)="data">
              <router-link
                :event="data.value !== '' ? 'click' : ''"
                :to="{
                    path: '/shipment/' + data.value
                  }"
                >
                {{ data.value }}
              </router-link>
            </template>
          </b-table>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Chart from 'chart.js';
import * as d3 from 'd3';
// import { getChartAnalytics, getTablleAnalytics } from '../api/analytics';

export default {
  name: 'BookingAnalytics',
  data() {
    return {
      outstandingBalances: 0,
      totalRevenue: 0,
      balanceByDestination: {
        labels: [],
        data: [],
        colors: d3.schemeTableau10,
      },
      revenueByDestination: {
        labels: [],
        data: [],
        colors: d3.schemeTableau10,
      },
      avgDaysPastDue: { labels: [], data: [] },
      topPaymentsByDestination: { labels: [], datasets: [] },
      topOverdueBookings: [],
      topPaidBookings: [],
      tableData: [],

      // Filters
      filters: {
        manifestId: '',
        dateRange: '',
        paymentMethods: ['Cash', 'Credit Card', 'Bank Transfer', 'Other'],
      },

      // Filter options
      dateRangeOptions: [
        { text: 'Last 30 Days', value: 'last30' },
        { text: 'This Quarter', value: 'quarter' },
        { text: 'Custom Range', value: 'custom' },
      ],
      paymentMethods: [
        { text: 'Cash', value: 'Cash' },
        { text: 'Credit Card', value: 'Credit Card' },
        { text: 'Check/Bank Transfer', value: 'Bank Transfer' },
        { text: 'Other', value: 'Other' },
      ],

      // Table fields
      tableFields: [
        { key: 'invoiceId', label: 'Invoice ID', sortable: true },
        { key: 'manifestId', label: 'Manifest ID', sortable: true },
        { key: 'client', label: 'Client', sortable: true },
        { key: 'due', label: 'Due ($)', sortable: true },
        { key: 'received', label: 'Received ($)', sortable: true },
        { key: 'dateReceived', label: 'Date Received', sortable: true },
        { key: 'method', label: 'Method', sortable: true },
        { key: 'processedBy', label: 'Processed By', sortable: true },
      ],

      // Chart instances
      charts: {},
    };
  },
  computed: {
    filteredTableData() {
      let filtered = [...this.tableData];

      // Filter by manifest ID
      if (this.filters.manifestId) {
        const searchTerm = this.filters.manifestId.toLowerCase();
        filtered = filtered.filter((item) => item.manifestId.toLowerCase().includes(searchTerm));
      }

      // // Filter by payment method
      // if (
      //   this.filters.paymentMethods
      //   && this.filters.paymentMethods.length > 0
      // ) {
      //   filtered = filtered.filter((item) => {
      //     if (item.method === '-') return false;
      //     return this.filters.paymentMethods.includes(item.method);
      //   });
      // }
      return filtered;
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return `$${Number(value).toLocaleString()}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString();
    },
    async loadAnalyticsData() {
      try {
        // Load analytics data for charts
        const analyticsRes = await fetch(
          'https://gunter-backend.soonforward.us/api/analytics/bookings',
        );

        if (!analyticsRes.ok) throw new Error('Analytics API returned error');
        const analyticsData = await analyticsRes.json();
        this.applyAnalyticsData(analyticsData);

        // Load table data separately
        const tableRes = await fetch(
          'https://gunter-backend.soonforward.us/api/analytics/table',
        );
        if (!tableRes.ok) throw new Error('Table API returned error');
        const tableData = await tableRes.json();
        this.tableData = tableData;
      } catch (err) {
        console.error('API failed:', err);
      }

      this.$nextTick(() => {
        this.initCharts();
      });
    },

    applyAnalyticsData(data) {
      // 1. BALANCE BY DESTINATION
      this.balanceByDestination = {
        labels: data.balanceByDestination.map(
          (d) => d.destination || 'Unknown',
        ),
        data: data.balanceByDestination.map((d) => Number(d.total_due) || 0),
        colors: d3.schemeTableau10,
      };

      this.outstandingBalances = this.balanceByDestination.data.reduce(
        (a, b) => a + b,
        0,
      );

      // 2. AVG DAYS PAST DUE
      this.avgDaysPastDue = {
        labels: data.avgDaysPastDue.map((d) => d.destination),
        data: data.avgDaysPastDue.map((d) => Number(d.avg_days)),
      };

      // 3. REVENUE BY DESTINATION
      this.revenueByDestination = {
        labels: data.revenueByDestination.map((d) => d.destination),
        data: data.revenueByDestination.map((d) => Number(d.revenue)),
        colors: d3.schemeTableau10,
      };

      this.totalRevenue = this.revenueByDestination.data.reduce(
        (a, b) => a + b,
        0,
      );

      // 4. TOP PAYMENTS BY DESTINATION
      const tp = data.topPaymentsByDestination || {
        labels: ['Cash', 'Credit Card', 'Bank Transfer', 'Other'],
        datasets: [],
      };

      this.topPaymentsByDestination = {
        labels: tp.labels,
        datasets: tp.datasets.map((d, i) => ({
          label: d.label,
          data: d.data,
          backgroundColor: d3.schemeTableau10[i],
          borderWidth: 1,
        })),
      };

      // 5. TOP OVERDUE LIST
      this.topOverdueBookings = (data.topOverdue || []).map((d) => ({
        client: d.destination,
        manifestId: d.shipment_id,
        due: Number(d.due || 0),
        daysPastDue: Number(d.days_past_due || 0),
      }));

      // 6. TOP PAID LIST
      this.topPaidBookings = (data.topPaid || []).map((d) => ({
        client: d.destination,
        manifestId: d.shipment_id,
        received: Number(d.received || 0),
        dateReceived: d.date_received,
      }));
    },

    initCharts() {
      // Register donut chart center text plugin (reuse from Dashboard)
      Chart.pluginService.register({
        beforeDraw(chart) {
          if (
            chart.config.options.elements
            && chart.config.options.elements.center
          ) {
            const { ctx } = chart.chart;
            const centerConfig = chart.config.options.elements.center;
            const fontStyle = centerConfig.fontStyle || 'Arial';
            const txt = centerConfig.text;
            const color = centerConfig.color || '#000';
            const maxFontSize = centerConfig.maxFontSize || 75;
            const sidePadding = centerConfig.sidePadding || 20;
            const sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);

            ctx.font = `30px ${fontStyle}`;
            const stringWidth = ctx.measureText(txt).width;
            const elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;

            const widthRatio = elementWidth / stringWidth;
            const newFontSize = Math.floor(30 * widthRatio);
            const elementHeight = chart.innerRadius * 2;

            let fontSizeToUse = Math.min(
              newFontSize,
              elementHeight,
              maxFontSize,
            );
            let { minFontSize } = centerConfig;
            const lineHeight = centerConfig.lineHeight || 25;
            let wrapText = false;

            if (minFontSize === undefined) {
              minFontSize = 20;
            }

            if (minFontSize && fontSizeToUse < minFontSize) {
              fontSizeToUse = minFontSize;
              wrapText = true;
            }

            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
            let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
            ctx.font = `${fontSizeToUse}px ${fontStyle}`;
            ctx.fillStyle = color;

            if (!wrapText) {
              ctx.fillText(txt, centerX, centerY);
              return;
            }

            const words = txt.split(' ');
            let line = ' ';
            const lines = [];

            for (let n = 0; n < words.length; n += 1) {
              const testLine = `${line + words[n]} `;
              const metrics = ctx.measureText(testLine);
              const testWidth = metrics.width;
              if (testWidth > elementWidth && n > 0) {
                lines.push(line);
                line = `${words[n]} `;
              } else {
                line = testLine;
              }
            }

            centerY -= (lines.length / 2) * lineHeight;

            for (let n = 0; n < lines.length; n += 1) {
              ctx.fillText(lines[n], centerX, centerY);
              centerY += lineHeight;
            }
            ctx.fillText(line, centerX, centerY);
          }
        },
      });

      // Outstanding Balances by Destination donut chart
      const balanceCtx = document
        .getElementById('balance-by-destination-chart')
        .getContext('2d');
      this.charts.balanceByDestination = new Chart(balanceCtx, {
        type: 'doughnut',
        data: {
          labels: this.balanceByDestination.labels,
          datasets: [
            {
              data: this.balanceByDestination.data,
              backgroundColor: this.balanceByDestination.colors,
            },
          ],
        },
        options: {
          elements: {
            center: {
              text: d3.format('~s')(this.outstandingBalances),
              color: 'rgb(107, 114, 128)',
              fontStyle: 'Arial',
              sidePadding: 40,
              minFontSize: 16,
              lineHeight: 20,
            },
          },
          cutoutPercentage: 75,
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            align: 'center',
            labels: {
              boxWidth: 12,
              padding: 8,
              fontSize: 10,
            },
          },
          tooltips: {
            callbacks: {
              label(tooltipItem, data) {
                const label = data.labels[tooltipItem.index] || '';
                const value = data.datasets[0].data[tooltipItem.index];
                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${label}: $${d3.format(',.0f')(
                  value,
                )} (${percentage}%)`;
              },
            },
          },
        },
      });

      // Avg Days Past Due horizontal bar chart
      const avgDaysCtx = document
        .getElementById('avg-days-past-due-chart')
        .getContext('2d');
      this.charts.avgDaysPastDue = new Chart(avgDaysCtx, {
        type: 'horizontalBar',
        data: {
          labels: this.avgDaysPastDue.labels,
          datasets: [
            {
              label: 'Days',
              data: this.avgDaysPastDue.data,
              backgroundColor: d3.schemeTableau10,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  max: 30,
                },
                gridLines: {
                  display: false,
                },
              },
            ],
            yAxes: [
              {
                gridLines: {
                  display: false,
                },
              },
            ],
          },
          legend: {
            display: false,
          },
          tooltips: {
            callbacks: {
              label(tooltipItem) {
                return `${tooltipItem.value} days`;
              },
            },
          },
        },
      });

      // Total Revenue by Destination donut chart
      const revenueByDestCtx = document
        .getElementById('revenue-by-destination-chart')
        .getContext('2d');
      this.charts.revenueByDestination = new Chart(revenueByDestCtx, {
        type: 'doughnut',
        data: {
          labels: this.revenueByDestination.labels,
          datasets: [
            {
              data: this.revenueByDestination.data,
              backgroundColor: this.revenueByDestination.colors,
            },
          ],
        },
        options: {
          elements: {
            center: {
              text: d3.format('~s')(this.totalRevenue),
              color: 'rgb(107, 114, 128)',
              fontStyle: 'Arial',
              sidePadding: 40,
              minFontSize: 16,
              lineHeight: 20,
            },
          },
          cutoutPercentage: 75,
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            align: 'center',
            labels: {
              boxWidth: 12,
              padding: 8,
              fontSize: 10,
            },
          },
          tooltips: {
            callbacks: {
              label(tooltipItem, data) {
                const label = data.labels[tooltipItem.index] || '';
                const value = data.datasets[0].data[tooltipItem.index];
                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${label}: $${d3.format(',.0f')(
                  value,
                )} (${percentage}%)`;
              },
            },
          },
        },
      });

      // Top Payments by Destination - horizontal stacked bar chart
      const topPaymentsCtx = document
        .getElementById('top-payments-chart')
        .getContext('2d');

      // Destroy old chart if exists to prevent overlapping
      if (this.charts.topPayments) {
        this.charts.topPayments.destroy();
      }

      this.charts.topPayments = new Chart(topPaymentsCtx, {
        type: 'horizontalBar',
        data: {
          labels: this.topPaymentsByDestination.labels, // payment methods (Y)
          datasets: this.topPaymentsByDestination.datasets, // destinations (colored)
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,

          scales: {
            xAxes: [
              {
                stacked: true,
                ticks: {
                  beginAtZero: true,
                  callback(value) {
                    return `$${d3.format(',.0f')(value)}`;
                  },
                },
                gridLines: {
                  display: false,
                },
              },
            ],
            yAxes: [
              {
                stacked: true,
                gridLines: {
                  display: false,
                },
              },
            ],
          },

          legend: {
            position: 'top',
            labels: {
              boxWidth: 12,
              padding: 8,
              fontSize: 10,
            },
          },

          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title(tooltipItems) {
                // Y-axis label → PAYMENT METHOD
                return tooltipItems[0].yLabel || '';
              },
              label(tooltipItem, data) {
                // Destination label (country)
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const destination = dataset.label;
                const amount = tooltipItem.xLabel || 0;
                return `${destination}: $${d3.format(',.0f')(amount)}`;
              },
            },
          },
        },
      });
    },
  },
  // mounted() {
  // Initialize all charts
  //  this.$nextTick(() => {
  //    this.initCharts();
  //  });
  // },
  mounted() {
    this.loadAnalyticsData();
  },

  beforeDestroy() {
    // Destroy all chart instances
    Object.values(this.charts).forEach((chart) => {
      if (chart) {
        chart.destroy();
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.booking-analytics {
  min-height: 100vh;
}

.rounded-lg {
  border-radius: 8px;
}

.top-bookings-list {
  max-height: 180px;
  overflow-y: auto;
}

.top-booking-item {
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.top-booking-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

::v-deep .analytics-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

::v-deep .analytics-table th {
  border-top: none;
  padding: 1.25rem 0.75rem;
  background-color: #f9fafb;
  font-weight: 600;
  color: var(--font-color);
  font-size: 0.875rem;
}

::v-deep .analytics-table td {
  padding: 1rem 0.75rem;
  border-top: 1px solid #e5e7eb;
}

::v-deep .analytics-table tbody tr:hover {
  background-color: #f9fafb;
}

::v-deep .form-control {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
}

::v-deep .form-control:focus {
  border-color: var(--hover-color);
  box-shadow: 0 0 0 0.2rem rgba(118, 145, 179, 0.25);
}

::v-deep .custom-radio,
::v-deep .custom-checkbox {
  margin-bottom: 0.5rem;
}

::v-deep .custom-control-label {
  cursor: pointer;
  color: var(--font-color);
}
</style>
